import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { MainLayout } from "../components/layout/MainLayout";
import { Header } from "../components/layout/Header";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Plus,
  Trash2,
  Save,
  AlertCircle,
  ChefHat,
  Package,
  ArrowLeft,
} from "lucide-react";
import { useToast } from "../components/ui/use-toast";
import ReactSelect from "react-select";
import {
  server_post_data,
  get_view_category,
  get_all_menu,
  get_view_menu,
  save_update_receipe,
  get_ingredients,
  get_view_units,
  get_recipe_details,
  get_addon_data_res,
  get_recipes_as_ingredients,
  get_department_with_categories,
} from "../ServiceConnection/serviceconnection";
import Loader from "../components/layout/Loader";

/* ------------------------------------------------------------------ */
/* helpers                                                             */
/* ------------------------------------------------------------------ */

let ROW_SEQ = 0;
const nextRowId = () => `row_${Date.now()}_${ROW_SEQ++}`;

const num = (v, d = 0) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : d;
};

const blankRow = () => ({
  id: nextRowId(),
  ingredient: null,
  quantity: "",

  // unit selection
  unitValue: "", // dropdown value: unit id as string, or "ru"
  unitId: "", // real Units.primary_id sent to backend
  unitLabel: "",
  parentUnitId: "",
  baseUnitId: "",

  // recipe unit (Spoon / Leaf / Piece)
  isRecipeUnit: false,
  recipeUnitName: "",
  recipeUnitQty: 0, // 1 recipe unit = X base unit
  conversionUsed: 0, // frozen rate (edit mode)

  // yield / waste
  yield: "",
  wasteQuantity: "",

  // sub recipe
  sourceRecipeId: 0,
  isSubRecipe: false,
  subPreparedQty: 0,
  subPreparedUnitId: "",
  subPreparedUnitName: "",
});

export default function AddEditRecipe() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { id } = useParams();
  const primary_id = id ? parseInt(id) : null;
  const isEditMode = !!primary_id;

  /* ---------------- state ---------------- */
  const [showLoaderAdmin, setShowLoaderAdmin] = useState(false);
  const [recipeName, setRecipeName] = useState("");
  const [recipeCode, setRecipeCode] = useState("RCP-001");
  const [isActive, setIsActive] = useState(true);
  const [recipeType, setRecipeType] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedMenuItem, setSelectedMenuItem] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAddon, setSelectedAddon] = useState(null);
  const [selectedAddonItem, setSelectedAddonItem] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const [category, setCategory] = useState([]);
  const [menuData, setMenuData] = useState({ items: [], addon: [] });
  const [varData, setVarData] = useState([]);
  const [backendIngredients, setBackendIngredients] = useState([]);
  const [addonItemOptions, setAddonItemOptions] = useState([]);
  const [units, setUnits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [subRecipeOptions, setSubRecipeOptions] = useState([]);

  const [preparedQty, setPreparedQty] = useState("");
  const [preparedUnit, setPreparedUnit] = useState(null);

  const [isLoadingData, setIsLoadingData] = useState(true);
  const [errors, setErrors] = useState({});

  const [ingredients, setIngredients] = useState([blankRow()]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ingredientToDelete, setIngredientToDelete] = useState(null);

  const editLoadedRef = useRef(false);
  // addon jaldi-jaldi badlne pe purana response naye ko overwrite na kare
  const addonReqRef = useRef(0);

  const recipeTypeOptions = [
    { value: "Item", label: "Item" },
    { value: "Addon", label: "Addon" },
    { value: "Sub Recipe", label: "Sub Recipe" },
  ];

  // Department sirf Item pe. Addon aur Sub Recipe parent dish se inherit karte hain.
  const needsDepartment = recipeType?.value === "Item";

  /* ---------------- react-select styles ---------------- */
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "40px",
      fontSize: "14px",
      borderRadius: "0.5rem",
      borderWidth: "1px",
      borderColor: state.isFocused ? "hsl(var(--ring))" : "hsl(var(--input))",
      backgroundColor: state.isDisabled ? "hsl(var(--muted))" : "transparent",
      boxShadow: state.isFocused ? "0 0 0 1px hsl(var(--ring))" : "none",
      cursor: state.isDisabled ? "not-allowed" : "default",
      "&:hover": { borderColor: "hsl(var(--input))" },
      transition: "all 0.2s",
    }),
    valueContainer: (base) => ({ ...base, padding: "2px 12px" }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
      color: "hsl(var(--foreground))",
    }),
    placeholder: (base) => ({
      ...base,
      color: "hsl(var(--muted-foreground))",
      fontSize: "14px",
    }),
    singleValue: (base) => ({
      ...base,
      color: "hsl(var(--foreground))",
      fontSize: "14px",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "0.5rem",
      marginTop: "4px",
      border: "1px solid hsl(var(--border))",
      backgroundColor: "hsl(var(--popover))",
      boxShadow:
        "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
      zIndex: 9999,
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menuList: (base) => ({ ...base, padding: "4px", maxHeight: "250px" }),
    option: (base, state) => ({
      ...base,
      fontSize: "14px",
      borderRadius: "0.375rem",
      padding: "8px 12px",
      margin: "2px 0",
      backgroundColor: state.isSelected
        ? "hsl(var(--primary))"
        : state.isFocused
          ? "hsl(var(--accent))"
          : "transparent",
      color: state.isSelected
        ? "hsl(var(--primary-foreground))"
        : "hsl(var(--foreground))",
      cursor: "pointer",
      "&:active": { backgroundColor: "hsl(var(--primary))" },
    }),
    indicatorSeparator: (base) => ({
      ...base,
      backgroundColor: "hsl(var(--border))",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "hsl(var(--muted-foreground))",
      "&:hover": { color: "hsl(var(--foreground))" },
    }),
    clearIndicator: (base) => ({
      ...base,
      color: "hsl(var(--muted-foreground))",
      "&:hover": { color: "hsl(var(--foreground))" },
    }),
  };

  /* ---------------- fetchers ---------------- */

  const fetchCategories = useCallback(async () => {
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");
    try {
      const res = await server_post_data(get_view_category, fd);
      if (!res.data.error) setCategory(res.data.message || []);
    } catch (e) {
      console.error("categories:", e);
    }
  }, []);

  const fetchMenuItems = useCallback(async () => {
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");
    try {
      const res = await server_post_data(get_all_menu, fd);
      if (!res.data.error) {
        const d = res.data.message || {};
        setMenuData({ items: d.items || [], addon: d.addon || [] });
      }
    } catch (e) {
      console.error("menu:", e);
    }
  }, []);

  const fetchDepartments = useCallback(async () => {
    const fd = new FormData();
    try {
      const res = await server_post_data(get_department_with_categories, fd);
      if (!res.data.error) setDepartments(res.data.message?.data || []);
    } catch (e) {
      console.error("departments:", e);
    }
  }, []);

  const fetchVariants = useCallback(async (menuItemId) => {
    const fd = new FormData();
    fd.append("call_id", parseInt(menuItemId));
    fd.append("flag", 2);
    try {
      const res = await server_post_data(get_view_menu, fd);
      if (!res.data.error) {
        const list = res.data.message?.variationlist || [];
        setVarData(list);
        return list;
      }
    } catch (e) {
      console.error("variants:", e);
    }
    return [];
  }, []);

  const fetchIngredients = useCallback(async () => {
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");
    try {
      const res = await server_post_data(get_ingredients, fd);
      if (!res.data.error && res.data.message) {
        const raw = res.data.message;
        const arr = raw?.final_data || raw || [];
        setBackendIngredients(
          Array.isArray(arr)
            ? arr.map((ing) => ({
                primary_id: ing.primary_id,
                ingredient_name: ing.ingredient_name,
                default_unit_id: ing.default_unit_id,
                recipe_unit_active: num(ing.recipe_unit_active, 0),
                recipe_unit_name: ing.recipe_unit_name || "",
                recipe_unit_qty_in_base: num(ing.recipe_unit_qty_in_base, 0),
              }))
            : [],
        );
      }
    } catch (e) {
      console.error("ingredients:", e);
    }
  }, []);

  const fetchUnits = useCallback(async () => {
    const fd = new FormData();
    fd.append("call_id", "0");
    fd.append("flag", "0");
    try {
      const res = await server_post_data(get_view_units, fd);
      if (!res.data.error && res.data.message) {
        const raw = res.data.message;
        const arr = raw?.final_data || raw || [];
        setUnits(
          Array.isArray(arr)
            ? arr.map((u) => ({
                primary_id: u.primary_id,
                unit_name: u.unit_name,
                short_code: u.short_code_name || u.unit_name,
                parent_unit_id: u.parent_unit_id,
              }))
            : [],
        );
      }
    } catch (e) {
      console.error("units:", e);
    }
  }, []);

  const fetchSubRecipes = useCallback(async () => {
    const fd = new FormData();
    fd.append("current_recipe_id", primary_id || "0");
    try {
      const res = await server_post_data(get_recipes_as_ingredients, fd);
      if (!res.data.error) {
        setSubRecipeOptions(
          (res.data.message || []).map((r) => ({
            value: r.primary_id,
            label: `[Recipe] ${r.recipe_name}`,
            isSubRecipe: true,
            output_qty: num(r.recipe_output_qty, 1),
            output_unit_id: r.recipe_output_unit_id,
            output_unit_name: r.recipe_output_unit_name,
          })),
        );
      }
    } catch (e) {
      console.error("sub-recipes:", e);
    }
  }, [primary_id]);

  // addon group ke items — sirf data return, state nahi chhoota (edit load ke liye)
  const loadAddonItemOptions = useCallback(async (addonId) => {
    const fd = new FormData();
    fd.append("check_flag", "1");
    fd.append("addon_id", addonId);
    fd.append("flag", "0");
    try {
      const res = await server_post_data(get_addon_data_res, fd);
      if (!res.data.error && Array.isArray(res.data.message?.items)) {
        return res.data.message.items.map((a) => ({
          value: a.primary_id,
          label: a.item,
        }));
      }
    } catch (e) {
      console.error("addon items:", e);
    }
    return [];
  }, []);

  const fetchAddonItems = useCallback(async (addonId) => {
    const reqId = ++addonReqRef.current;
    const fd = new FormData();
    fd.append("check_flag", "1");
    fd.append("addon_id", addonId);
    fd.append("flag", "0");
    try {
      const res = await server_post_data(get_addon_data_res, fd);
      if (reqId !== addonReqRef.current) return []; // stale response
      if (!res.data.error && res.data.message?.items?.length) {
        const opts = res.data.message.items.map((a) => ({
          value: a.primary_id,
          label: a.item,
        }));
        setAddonItemOptions(opts);
        return opts;
      }
      setAddonItemOptions([]);
    } catch (e) {
      console.error("addon items:", e);
      if (reqId === addonReqRef.current) setAddonItemOptions([]);
    }
    return [];
  }, []);

  /* ---------------- unit helpers ---------------- */

  const unitById = useCallback(
    (uid) => units.find((u) => Number(u.primary_id) === Number(uid)),
    [units],
  );

  // Sub-recipe row -> sirf prepared unit ki family (ML/Gram mix rokna)
  // Raw row        -> ingredient ke default unit ki family + recipe unit
  const getUnitOptionsForRow = useCallback(
    (row) => {
      const opts = [];
      const anchorId = row.isSubRecipe
        ? row.subPreparedUnitId
        : row.baseUnitId || row.unitId;

      const anchor = unitById(anchorId);
      if (anchor) {
        units
          .filter(
            (u) => Number(u.parent_unit_id) === Number(anchor.parent_unit_id),
          )
          .forEach((u) =>
            opts.push({ value: String(u.primary_id), label: u.short_code }),
          );
      }

      if (!row.isSubRecipe && row.recipeUnitName && row.recipeUnitQty > 0) {
        const baseName = unitById(row.baseUnitId)?.short_code || "";
        opts.push({
          value: "ru",
          label: `${row.recipeUnitName} (1 = ${row.recipeUnitQty} ${baseName})`,
        });
      }
      return opts;
    },
    [units, unitById],
  );

  const generateRecipeCode = (itemName = "", variantName = "") => {
    if (!itemName) return "RCP-001";
    const itemPart = itemName
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 3);
    const variantPart = variantName
      ? variantName
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "")
          .slice(0, 2)
      : "";
    const prefix = (itemPart + variantPart).padEnd(3, "X");
    return `${prefix}-${String(Math.floor(Math.random() * 900) + 100)}`;
  };

  /* ---------------- options ---------------- */

  const ingredientOptions = useMemo(() => {
    const raw = backendIngredients.map((ing) => ({
      value: ing.primary_id,
      label: ing.ingredient_name,
      unitId: ing.default_unit_id,
      isSubRecipe: false,
    }));
    const groups = [{ label: "── Raw Ingredients ──", options: raw }];
    if (subRecipeOptions.length) {
      groups.push({
        label: "── Semi-Finished / Sub-Recipes ──",
        options: subRecipeOptions,
      });
    }
    return groups;
  }, [backendIngredients, subRecipeOptions]);

  // category select ho to sirf usi ke items (0 items = khali list, sab nahi)
  const menuItemOptions = useMemo(() => {
    const list = selectedCategory
      ? menuData.items.filter(
          (i) => Number(i.cate_id) === Number(selectedCategory.value),
        )
      : menuData.items;
    return list.map((i) => ({ value: i.primary_id, label: i.item_name }));
  }, [menuData.items, selectedCategory]);

  const addonOptions = useMemo(
    () =>
      (menuData.addon || []).map((a) => ({
        value: a.primary_id,
        label: a.addon_name,
      })),
    [menuData.addon],
  );

  const variantOptions = useMemo(
    () =>
      (varData || []).map((v) => ({
        value: v.primary_id,
        label: v.variation_name || v.name || "Unnamed Variant",
      })),
    [varData],
  );

  const departmentOptions = useMemo(
    () =>
      (departments || []).map((d) => ({
        value: d.department_id,
        label: d.department_name,
        isMainStore: d.is_main_store === 1,
      })),
    [departments],
  );

  /* ---------------- handlers: header ---------------- */

  const handleRecipeTypeChange = (value) => {
    setRecipeType({ value, label: value });
    setSelectedCategory(null);
    setSelectedMenuItem(null);
    setSelectedVariant(null);
    setSelectedAddon(null);
    setSelectedAddonItem(null);
    setAddonItemOptions([]);
    addonReqRef.current++; // pending addon request ignore
    setRecipeName("");
    setVarData([]);
    setPreparedUnit(null);

    if (value === "Addon") {
      setRecipeCode("ARP-001");
      setPreparedQty("");
    } else if (value === "Sub Recipe") {
      setRecipeCode("SUB-001");
      setPreparedQty("1");
    } else {
      setRecipeCode("RCP-001");
      setPreparedQty("");
    }
    setErrors({});
  };

  const handleCategoryChange = (opt) => {
    setSelectedCategory(opt);
    if (!opt) {
      setSelectedMenuItem(null);
      setSelectedVariant(null);
      setVarData([]);
      setRecipeName("");
      setRecipeCode("RCP-001");
      return;
    }
    setSelectedMenuItem(null);
    setVarData([]);
    setSelectedVariant(null);
    setRecipeName("");
    setRecipeCode("RCP-001");
  };

  const handleMenuItemChange = (opt) => {
    setSelectedMenuItem(opt);
    setSelectedVariant(null);
    if (!opt) {
      setRecipeName("");
      setRecipeCode("RCP-001");
      setVarData([]);
      return;
    }
    const menuItem = menuData.items.find((i) => i.primary_id === opt.value);
    if (menuItem) {
      const cat = category.find((c) => c.primary_id === menuItem.cate_id);
      if (cat) {
        setSelectedCategory({
          value: cat.primary_id,
          label: cat.item_category || cat.name || "Unknown",
        });
      }
    }
    setRecipeName(opt.label);
    fetchVariants(opt.value);
    setRecipeCode(generateRecipeCode(opt.label));
  };

  const handleVariantChange = (opt) => {
    setSelectedVariant(opt);
    if (!selectedMenuItem) return;
    if (!opt) {
      setRecipeName(selectedMenuItem.label);
      setRecipeCode(generateRecipeCode(selectedMenuItem.label));
      return;
    }
    setRecipeName(`${selectedMenuItem.label} - ${opt.label}`);
    setRecipeCode(generateRecipeCode(selectedMenuItem.label, opt.label));
  };

  const handleAddonChange = (opt) => {
    setSelectedAddon(opt);
    setSelectedAddonItem(null);
    setAddonItemOptions([]);
    if (!opt) {
      addonReqRef.current++; // pending addon request ignore
      setRecipeName("");
      setRecipeCode("ARP-001");
      return;
    }
    setRecipeName("");
    setRecipeCode("ARP-001");
    fetchAddonItems(opt.value);
  };

  const handleAddonItemChange = (opt) => {
    setSelectedAddonItem(opt);
    if (!opt) {
      setRecipeName("");
      setRecipeCode("ARP-001");
      return;
    }
    setRecipeName(opt.label);
    setRecipeCode(generateRecipeCode(opt.label));
  };

  /* ---------------- handlers: ingredient rows ---------------- */

  const patchRow = (index, patch) =>
    setIngredients((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );

  const handleIngredientChange = useCallback(
    (index, opt) => {
      if (!opt) {
        setIngredients((prev) =>
          prev.map((r, i) => (i === index ? blankRow() : r)),
        );
        return;
      }

      // ---- SUB RECIPE ----
      if (opt.isSubRecipe === true) {
        const u = unitById(opt.output_unit_id);
        patchRow(index, {
          ingredient: opt,
          sourceRecipeId: opt.value,
          isSubRecipe: true,
          subPreparedQty: num(opt.output_qty, 1),
          subPreparedUnitId: opt.output_unit_id,
          subPreparedUnitName: u?.short_code || opt.output_unit_name || "",
          isRecipeUnit: false,
          recipeUnitName: "",
          recipeUnitQty: 0,
          conversionUsed: 0,
          unitValue: u ? String(u.primary_id) : "",
          unitId: u?.primary_id || "",
          unitLabel: u?.short_code || opt.output_unit_name || "",
          parentUnitId: u?.parent_unit_id || "",
          baseUnitId: u?.primary_id || "",
          quantity: "",
          yield: "",
          wasteQuantity: "",
        });
        return;
      }

      // ---- RAW INGREDIENT ----
      const master = backendIngredients.find(
        (x) => Number(x.primary_id) === Number(opt.value),
      );
      const defUnit = unitById(master?.default_unit_id);

      const hasRecipeUnit =
        master?.recipe_unit_active === 1 &&
        master?.recipe_unit_name &&
        num(master?.recipe_unit_qty_in_base) > 0;

      patchRow(index, {
        ingredient: opt,
        sourceRecipeId: 0,
        isSubRecipe: false,
        subPreparedQty: 0,
        subPreparedUnitId: "",
        subPreparedUnitName: "",
        recipeUnitName: hasRecipeUnit ? master.recipe_unit_name : "",
        recipeUnitQty: hasRecipeUnit ? num(master.recipe_unit_qty_in_base) : 0,
        conversionUsed: 0,
        baseUnitId: defUnit?.primary_id || "",
        isRecipeUnit: false,
        unitValue: defUnit ? String(defUnit.primary_id) : "",
        unitId: defUnit?.primary_id || "",
        unitLabel: defUnit?.short_code || "",
        parentUnitId: defUnit?.parent_unit_id || "",
        quantity: "",
        yield: "",
        wasteQuantity: "",
      });
    },
    [backendIngredients, unitById],
  );

  const handleUnitChange = (index, value) => {
    const row = ingredients[index];
    if (!row) return;

    if (value === "ru") {
      patchRow(index, {
        unitValue: "ru",
        isRecipeUnit: true,
        unitId: row.baseUnitId || row.unitId,
        unitLabel: row.recipeUnitName,
        yield: "",
        wasteQuantity: "",
      });
      return;
    }

    const u = unitById(value);
    if (!u) return;

    // unit badla (KG -> G / Leaf -> G) to purana yield/waste purane unit me
    // tha — galat number na bache, isliye reset
    const unitChanged = row.unitValue !== String(u.primary_id);

    patchRow(index, {
      unitValue: String(u.primary_id),
      isRecipeUnit: false,
      unitId: u.primary_id,
      unitLabel: u.short_code,
      parentUnitId: u.parent_unit_id,
      ...(unitChanged ? { yield: "", wasteQuantity: "" } : {}),
    });
  };

  const calcWaste = (qty, yld) => {
    const q = num(qty);
    const y = num(yld);
    if (!q && !y) return "";
    return (q - y).toFixed(2);
  };

  const handleQuantityChange = (index, value) => {
    if (!/^\d*\.?\d{0,3}$/.test(value)) return;
    setIngredients((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        // recipe unit / sub-recipe pe yield-waste lagta hi nahi
        if (r.isRecipeUnit || r.isSubRecipe) return { ...r, quantity: value };
        const newYield = num(r.yield) > num(value) ? value : r.yield;
        return {
          ...r,
          quantity: value,
          yield: newYield,
          wasteQuantity: calcWaste(value, newYield),
        };
      }),
    );
  };

  const handleYieldChange = (index, value) => {
    if (!/^\d*\.?\d{0,3}$/.test(value)) return;
    const target = ingredients[index];
    if (!target || target.isRecipeUnit || target.isSubRecipe) return;
    setIngredients((prev) =>
      prev.map((r, i) => {
        if (i !== index) return r;
        const v = value === "." ? "0." : value;
        const finalYield = num(v) > num(r.quantity) ? r.quantity : v;
        return {
          ...r,
          yield: finalYield,
          wasteQuantity: calcWaste(r.quantity, finalYield),
        };
      }),
    );
  };

  const handleAddIngredient = () =>
    setIngredients((prev) => [...prev, blankRow()]);

  const handleDeleteIngredient = (index) => {
    if (ingredients.length === 1) {
      toast({
        title: "Warning",
        description: "At least one ingredient is required",
        variant: "destructive",
      });
      return;
    }
    setIngredientToDelete(index);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteIngredient = () => {
    if (ingredientToDelete !== null) {
      setIngredients((prev) => prev.filter((_, i) => i !== ingredientToDelete));
      setDeleteDialogOpen(false);
      setIngredientToDelete(null);
      toast({ title: "Success", description: "Ingredient removed" });
    }
  };

  /* ---------------- validation ---------------- */

  const isRowComplete = (r) =>
    !!r.ingredient && num(r.quantity) > 0 && !!r.unitId;

  const validateForm = () => {
    const e = {};

    if (!recipeType) e.recipeType = "Recipe type is required";

    if (recipeType?.value === "Item") {
      if (!selectedCategory) e.category = "Category is required";
      if (!selectedMenuItem) e.menuItem = "Menu item is required";
      if (!selectedDepartment)
        e.department = "Select where stock will be deducted from";
    }

    if (recipeType?.value === "Addon") {
      if (!selectedAddon) e.addon = "Addon is required";
      if (!selectedAddonItem) e.addonItem = "Addon item is required";
    }

    // backend Recipe.recipe_name max 50 chars
    if (recipeName.trim().length > 50) {
      e.recipeName = "Recipe name can be at most 50 characters";
    }

    if (recipeType?.value === "Sub Recipe") {
      if (!recipeName.trim()) e.recipeName = "Recipe name is required";
      if (num(preparedQty) <= 0)
        e.preparedQty = "Prepared quantity must be more than 0";
      if (!preparedUnit) e.preparedUnit = "Prepared unit is required";
    }

    const valid = ingredients.filter(isRowComplete);
    if (valid.length === 0) {
      e.ingredients = "Add at least one ingredient with a quantity and a unit";
    }

    // sub-recipe unit family check (Gram wali gravy ML me nahi ja sakti)
    for (const r of valid) {
      if (!r.isSubRecipe) continue;
      const rowUnit = unitById(r.unitId);
      const prepUnit = unitById(r.subPreparedUnitId);
      if (
        rowUnit &&
        prepUnit &&
        Number(rowUnit.parent_unit_id) !== Number(prepUnit.parent_unit_id)
      ) {
        e.ingredients = `${r.ingredient.label.replace(
          "[Recipe] ",
          "",
        )} is prepared in ${prepUnit.short_code}. Pick a matching unit.`;
        break;
      }
    }

    // duplicate row check
    const seen = new Set();
    for (const r of valid) {
      const key = r.isSubRecipe
        ? `sub_${r.sourceRecipeId}`
        : `raw_${r.ingredient.value}`;
      if (seen.has(key)) {
        e.ingredients = `"${r.ingredient.label}" is added more than once`;
        break;
      }
      seen.add(key);
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ---------------- save ---------------- */

  const handleSave = async () => {
    if (!validateForm()) {
      toast({
        title: "Check the form",
        description: "Some required fields are missing",
        variant: "destructive",
      });
      return;
    }

    setShowLoaderAdmin(true);

    const rows = ingredients.filter(isRowComplete);
    const fd = new FormData();

    fd.append("main_id", primary_id || "0");
    fd.append("recipe_code", recipeCode);
    fd.append("recipe_type", recipeType.value);
    fd.append("is_sub_recipe", recipeType.value === "Sub Recipe" ? "1" : "0");
    fd.append("active_status", isActive ? "1" : "0");

    fd.append(
      "department_id",
      needsDepartment ? selectedDepartment?.value || "0" : "0",
    );
    fd.append(
      "department_name",
      needsDepartment ? selectedDepartment?.label || "" : "",
    );

    if (recipeType.value === "Sub Recipe") {
      fd.append("recipe_name", recipeName.trim());
      fd.append("menu_or_addon_id", "0");
      fd.append("variant_id", "0");
      fd.append("variant_name", "");
      fd.append("category_id", "0");
      fd.append("recipe_output_qty", preparedQty || "1");
      fd.append("recipe_output_unit_id", preparedUnit?.value || "0");
      fd.append("recipe_output_unit_name", preparedUnit?.label || "");
    } else if (recipeType.value === "Addon") {
      fd.append("recipe_name", selectedAddonItem?.label || "");
      fd.append("menu_or_addon_id", selectedAddonItem?.value || "0");
      fd.append("variant_id", "0");
      fd.append("variant_name", "");
      fd.append("category_id", "0");
      fd.append("recipe_output_qty", "1");
      fd.append("recipe_output_unit_id", "0");
      fd.append("recipe_output_unit_name", "");
    } else {
      fd.append(
        "recipe_name",
        selectedVariant?.label
          ? `${selectedMenuItem.label} - ${selectedVariant.label}`
          : selectedMenuItem?.label || "",
      );
      fd.append("menu_or_addon_id", selectedMenuItem?.value || "0");
      fd.append("variant_id", selectedVariant?.value || "0");
      fd.append("variant_name", selectedVariant?.label || "");
      fd.append("category_id", selectedCategory?.value || "0");
      fd.append("recipe_output_qty", "1");
      fd.append("recipe_output_unit_id", "0");
      fd.append("recipe_output_unit_name", "");
    }

    rows.forEach((r) => {
      fd.append(
        "ingredient_name[]",
        r.isSubRecipe
          ? r.ingredient.label.replace("[Recipe] ", "")
          : r.ingredient.label,
      );
      fd.append("ingredient_id[]", r.isSubRecipe ? "0" : r.ingredient.value);
      fd.append("source_recipe_id[]", r.sourceRecipeId || "0");

      // quantity hamesha jo user ne type kiya — backend base me convert karega
      fd.append("quantity[]", r.quantity);
      fd.append("unit[]", r.unitId || "0");
      fd.append("unit_name[]", r.isRecipeUnit ? r.recipeUnitName : r.unitLabel);

      fd.append("is_recipe_unit[]", r.isRecipeUnit ? "1" : "0");
      fd.append("recipe_unit_name[]", r.isRecipeUnit ? r.recipeUnitName : "");

      // backend sub-recipe / recipe-unit row ka yield-waste save nahi karta
      const noYield = r.isRecipeUnit || r.isSubRecipe;
      fd.append("yield_quantity[]", noYield ? "0" : r.yield || "0");
      fd.append("yield_unit[]", "0");
      fd.append("yield_units_name[]", "");
      fd.append(
        "waste_quantity[]",
        noYield ? "0" : r.wasteQuantity || "0",
      );
    });

    try {
      const res = await server_post_data(save_update_receipe, fd);
      if (res.data.error) {
        toast({
          title: "Could not save",
          description: res.data.message || "Failed to save recipe",
          variant: "destructive",
        });
      } else {
        toast({
          title: "Saved",
          description: primary_id ? "Recipe updated" : "Recipe created",
        });
        navigate("/recipe-view");
      }
    } catch (err) {
      console.error("save:", err);
      toast({
        title: "Network error",
        description: "Could not reach the server. Try again.",
        variant: "destructive",
      });
    } finally {
      setShowLoaderAdmin(false);
    }
  };

  /* ---------------- edit mode load ---------------- */

  const fetchRecipeForEdit = useCallback(
    async (recipeId) => {
      setShowLoaderAdmin(true);
      const fd = new FormData();
      fd.append("recipe_id", recipeId);

      try {
        const res = await server_post_data(get_recipe_details, fd);
        if (res.data.error) {
          toast({
            title: "Could not load",
            description: res.data.message || "Failed to load recipe",
            variant: "destructive",
          });
          return;
        }

        const r = res.data.recipe || {};
        const ings = res.data.ingredients || [];

        setRecipeName(r.recipe_name || "");
        setRecipeCode(r.recipe_code || "");
        setIsActive(num(r.active_status, 1) === 1);
        setRecipeType({ label: r.recipe_type, value: r.recipe_type });

        if (r.recipe_type === "Sub Recipe") {
          setPreparedQty(String(r.recipe_output_qty ?? ""));
          if (r.recipe_output_unit_id) {
            const u = unitById(r.recipe_output_unit_id);
            setPreparedUnit({
              value: r.recipe_output_unit_id,
              label: u?.short_code || r.recipe_output_unit_name || "",
            });
          }
        }

        // department (sirf Item pe)
        if (num(r.department_id) > 0) {
          const d = departments.find(
            (x) => Number(x.department_id) === Number(r.department_id),
          );
          setSelectedDepartment({
            value: r.department_id,
            label: d?.department_name || r.department_name || "",
          });
        } else if (r.recipe_type === "Item") {
          const ms = departments.find((x) => x.is_main_store === 1);
          if (ms) {
            setSelectedDepartment({
              value: ms.department_id,
              label: ms.department_name,
            });
          }
        }

        const cat = category.find(
          (c) => Number(c.primary_id) === Number(r.category_id),
        );
        if (cat) {
          setSelectedCategory({
            value: cat.primary_id,
            label: cat.item_category || cat.name || "Unknown",
          });
        }

        if (num(r.menu_or_addon_id) > 0 && r.recipe_type === "Item") {
          const mi = menuData.items.find(
            (i) => Number(i.primary_id) === Number(r.menu_or_addon_id),
          );
          // get_all_menu sirf active items deta hai — inactive dish ki recipe
          // bhi edit ho sake isliye recipe ke data se fallback
          const variantSuffix = r.variant_name ? ` - ${r.variant_name}` : "";
          const fallbackLabel =
            r.menu_item_name ||
            (variantSuffix && (r.recipe_name || "").endsWith(variantSuffix)
              ? r.recipe_name.slice(0, -variantSuffix.length)
              : r.recipe_name) ||
            "";
          const menuId = mi ? mi.primary_id : r.menu_or_addon_id;
          setSelectedMenuItem({
            value: menuId,
            label: mi ? mi.item_name : fallbackLabel,
          });

          if (!cat && mi) {
            const c2 = category.find(
              (c) => Number(c.primary_id) === Number(mi.cate_id),
            );
            if (c2) {
              setSelectedCategory({
                value: c2.primary_id,
                label: c2.item_category || c2.name || "Unknown",
              });
            }
          }

          const vlist = await fetchVariants(menuId);
          if (num(r.variant_id) > 0) {
            const v = vlist.find(
              (x) => Number(x.primary_id) === Number(r.variant_id),
            );
            setSelectedVariant({
              value: r.variant_id,
              label: v?.variation_name || r.variant_name || "",
            });
          }
        }

        // ---- Addon: group + item dono set karo ----
        // menu_or_addon_id = addon ITEM id. Group id backend `addon_group_id`
        // deta hai; na aaye (purana backend) to groups me dhoondh lo.
        if (r.recipe_type === "Addon" && num(r.menu_or_addon_id) > 0) {
          const savedItemId = Number(r.menu_or_addon_id);
          const groups = menuData.addon || [];

          let groupId = num(r.addon_group_id);
          let itemOpts = [];

          if (groupId > 0) {
            itemOpts = await loadAddonItemOptions(groupId);
          } else {
            for (const g of groups) {
              const opts = await loadAddonItemOptions(g.primary_id);
              if (opts.some((o) => Number(o.value) === savedItemId)) {
                groupId = Number(g.primary_id);
                itemOpts = opts;
                break;
              }
            }
          }

          if (groupId > 0) {
            const grp = groups.find((a) => Number(a.primary_id) === groupId);
            const it = itemOpts.find((o) => Number(o.value) === savedItemId);
            const savedItem = {
              value: r.menu_or_addon_id,
              label: it?.label || r.addon_item_name || r.recipe_name || "",
            };

            // saved item list me na ho (inactive/deleted) tab bhi dikhe
            if (!it) itemOpts = [savedItem, ...itemOpts];

            addonReqRef.current++; // koi pending addon request ise overwrite na kare
            setSelectedAddon({
              value: groupId,
              label: grp?.addon_name || r.addon_group_name || "",
            });
            setAddonItemOptions(itemOpts);
            setSelectedAddonItem(savedItem);
          } else {
            toast({
              title: "Addon item not found",
              description:
                "The addon item for this recipe was deleted. Select a new one.",
              variant: "destructive",
            });
          }
        }

        // ---- ingredient rows ----
        const rows = ings.map((ing) => {
          const isSub = num(ing.source_recipe_id) > 0;
          const isRU = num(ing.is_recipe_unit) === 1;
          const conv = num(ing.conversion_used);

          const master = backendIngredients.find(
            (m) => Number(m.primary_id) === Number(ing.ingredient_id),
          );
          const sub = subRecipeOptions.find(
            (s) => Number(s.value) === Number(ing.source_recipe_id),
          );
          const unitObj = unitById(ing.item_unit_entry || ing.unit);

          // display qty: recipe unit -> frozen rate se, warna entered value
          let displayQty = ing.item_qty_entry;
          if (isRU && conv > 0) {
            displayQty = Math.round((num(ing.quantity) / conv) * 1000) / 1000;
          }
          if (
            displayQty === undefined ||
            displayQty === null ||
            displayQty === ""
          ) {
            displayQty = ing.quantity;
          }

          return {
            id: nextRowId(),
            ingredient: {
              value: isSub ? ing.source_recipe_id : ing.ingredient_id,
              label: isSub
                ? String(ing.ingredient_name || "").startsWith("[Recipe]")
                  ? ing.ingredient_name
                  : `[Recipe] ${ing.ingredient_name}`
                : ing.ingredient_name,
              isSubRecipe: isSub,
            },
            quantity: String(displayQty ?? ""),
            unitValue: isRU ? "ru" : unitObj ? String(unitObj.primary_id) : "",
            unitId: unitObj?.primary_id || ing.unit || "",
            unitLabel: isRU
              ? ing.recipe_unit_name || ""
              : unitObj?.short_code || ing.unit_name || "",
            parentUnitId: unitObj?.parent_unit_id || "",
            // base = ingredient ka default unit (recipe-unit label "1 = X G" sahi aaye)
            baseUnitId: isSub
              ? unitObj?.primary_id || ""
              : master?.default_unit_id || unitObj?.primary_id || "",

            isRecipeUnit: isRU,
            recipeUnitName:
              ing.recipe_unit_name || master?.recipe_unit_name || "",
            recipeUnitQty:
              conv > 0 ? conv : num(master?.recipe_unit_qty_in_base),
            conversionUsed: conv,

            yield: isRU || isSub ? "" : String(ing.yield_quantity ?? ""),
            wasteQuantity:
              isRU || isSub ? "" : String(ing.waste_quantity ?? ""),

            sourceRecipeId: isSub ? ing.source_recipe_id : 0,
            isSubRecipe: isSub,
            subPreparedQty: isSub
              ? num(sub?.output_qty ?? ing.sub_recipe_output_qty, 1)
              : 0,
            subPreparedUnitId: isSub
              ? sub?.output_unit_id || ing.sub_recipe_output_unit_id || ""
              : "",
            subPreparedUnitName: isSub
              ? sub?.output_unit_name || ing.sub_recipe_output_unit_name || ""
              : "",
          };
        });

        setIngredients(rows.length ? rows : [blankRow()]);
      } catch (err) {
        console.error("load recipe:", err);
        toast({
          title: "Could not load",
          description: "Something went wrong while loading the recipe",
          variant: "destructive",
        });
      } finally {
        setShowLoaderAdmin(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      units,
      category,
      menuData,
      departments,
      backendIngredients,
      subRecipeOptions,
    ],
  );

  /* ---------------- effects ---------------- */

  useEffect(() => {
    (async () => {
      setIsLoadingData(true);
      await Promise.all([
        fetchCategories(),
        fetchMenuItems(),
        fetchIngredients(),
        fetchUnits(),
        fetchSubRecipes(),
        fetchDepartments(),
      ]);
      setIsLoadingData(false);
    })();
  }, [
    fetchCategories,
    fetchMenuItems,
    fetchIngredients,
    fetchUnits,
    fetchSubRecipes,
    fetchDepartments,
  ]);

  // add mode: default department = Main Store
  useEffect(() => {
    if (isEditMode || selectedDepartment || departments.length === 0) return;
    const ms = departments.find((d) => d.is_main_store === 1);
    if (ms) {
      setSelectedDepartment({
        value: ms.department_id,
        label: ms.department_name,
      });
    }
  }, [departments, isEditMode, selectedDepartment]);

  // edit mode: sab master data aane ke baad ek hi baar
  useEffect(() => {
    if (
      primary_id &&
      !isLoadingData &&
      !editLoadedRef.current &&
      // category / menu / department khali ho sakte hain (sirf sub-recipe
      // wala outlet) — unpe rukne se edit kabhi load nahi hota tha
      units.length > 0
    ) {
      editLoadedRef.current = true;
      fetchRecipeForEdit(primary_id);
    }
  }, [
    primary_id,
    isLoadingData,
    units.length,
    fetchRecipeForEdit,
  ]);

  const handleCancel = () => navigate("/recipe-view");

  /* ---------------- render ---------------- */

  return (
    <MainLayout>
      <Header
        title={isEditMode ? "Edit Recipe" : "Add Recipe"}
        subtitle={
          isEditMode
            ? "Update recipe information and ingredients"
            : "Create a new recipe with ingredients"
        }
      />

      <div className="p-6">
        <div className="mx-auto space-y-6">
          <Button variant="ghost" onClick={handleCancel} className="gap-2 -ml-2">
            <ArrowLeft className="w-4 h-4" /> Back to Recipes
          </Button>

          {/* ---------- Recipe Information ---------- */}
          <Card>
            <CardHeader className="bg-muted/30">
              <CardTitle className="flex items-center gap-2">
                <ChefHat className="w-5 h-5" />
                Recipe Information
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Recipe Type */}
                <div className="space-y-2">
                  <Label>
                    Recipe Type <span className="text-destructive">*</span>
                  </Label>
                  <Select
                    value={recipeType?.value || ""}
                    onValueChange={handleRecipeTypeChange}
                    disabled={isLoadingData || isEditMode}
                  >
                    <SelectTrigger
                      className={errors.recipeType ? "border-destructive" : ""}
                    >
                      <SelectValue placeholder="Select Recipe Type" />
                    </SelectTrigger>
                    <SelectContent>
                      {recipeTypeOptions.map((t) => (
                        <SelectItem key={t.value} value={t.value}>
                          {t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.recipeType && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.recipeType}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Item, Addon, or a Sub Recipe used inside other recipes
                  </p>
                </div>

                {/* ---- Item fields ---- */}
                {recipeType?.value === "Item" && (
                  <>
                    <div className="space-y-2">
                      <Label>
                        Category <span className="text-destructive">*</span>
                      </Label>
                      <ReactSelect
                        value={selectedCategory}
                        onChange={handleCategoryChange}
                        options={category.map((c) => ({
                          value: c.primary_id,
                          label: c.item_category || c.name || "Unknown",
                        }))}
                        placeholder="Select Category"
                        isDisabled={category.length === 0 || isLoadingData}
                        isClearable
                        styles={customSelectStyles}
                        isLoading={isLoadingData}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {errors.category && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.category}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Auto-filled when you pick a menu item
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>
                        Menu Item <span className="text-destructive">*</span>
                      </Label>
                      <ReactSelect
                        value={selectedMenuItem}
                        onChange={handleMenuItemChange}
                        options={menuItemOptions}
                        placeholder="Select Menu Item"
                        isClearable
                        styles={customSelectStyles}
                        isLoading={isLoadingData}
                        isDisabled={isLoadingData}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {errors.menuItem && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.menuItem}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Variant (Optional)</Label>
                      <ReactSelect
                        value={selectedVariant}
                        onChange={handleVariantChange}
                        options={variantOptions}
                        placeholder={
                          !selectedMenuItem
                            ? "First select Menu Item"
                            : varData.length === 0
                              ? "No variants available"
                              : "Select Variant"
                        }
                        isDisabled={!selectedMenuItem || varData.length === 0}
                        isClearable={varData.length > 0}
                        styles={customSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      <p className="text-xs text-muted-foreground">
                        Half and Full need separate recipes
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>
                        Deduct Stock From{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <ReactSelect
                        value={selectedDepartment}
                        onChange={setSelectedDepartment}
                        options={departmentOptions}
                        placeholder="Select Department"
                        isDisabled={isLoadingData || departments.length === 0}
                        styles={customSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {errors.department && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.department}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        When this item is ordered, stock is cut from here
                      </p>
                    </div>
                  </>
                )}

                {/* ---- Addon fields ---- */}
                {recipeType?.value === "Addon" && (
                  <>
                    <div className="space-y-2">
                      <Label>
                        Addon <span className="text-destructive">*</span>
                      </Label>
                      <ReactSelect
                        value={selectedAddon}
                        onChange={handleAddonChange}
                        options={addonOptions}
                        placeholder="Select Addon"
                        isClearable
                        styles={customSelectStyles}
                        isLoading={isLoadingData}
                        isDisabled={isLoadingData}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {errors.addon && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.addon}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>
                        Addon Item <span className="text-destructive">*</span>
                      </Label>
                      <ReactSelect
                        value={selectedAddonItem}
                        onChange={handleAddonItemChange}
                        options={addonItemOptions}
                        placeholder={
                          !selectedAddon
                            ? "First select Addon"
                            : addonItemOptions.length === 0
                              ? "No addon items"
                              : "Select Addon Item"
                        }
                        isDisabled={!selectedAddon}
                        isClearable
                        styles={customSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {errors.addonItem && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.addonItem}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Stock is cut from the same department as the dish
                      </p>
                    </div>
                  </>
                )}

                {/* ---- Sub Recipe fields ---- */}
                {recipeType?.value === "Sub Recipe" && (
                  <>
                    <div className="space-y-2">
                      <Label>
                        Prepared Quantity{" "}
                        <span className="text-destructive">*</span>
                      </Label>
                      <Input
                        value={preparedQty}
                        onChange={(e) => {
                          if (/^\d*\.?\d{0,3}$/.test(e.target.value))
                            setPreparedQty(e.target.value);
                        }}
                        placeholder="e.g. 1000"
                        className={
                          errors.preparedQty ? "border-destructive" : ""
                        }
                      />
                      {errors.preparedQty && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.preparedQty}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        The ingredients below make this much in one go
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>
                        Prepared Unit <span className="text-destructive">*</span>
                      </Label>
                      <ReactSelect
                        value={preparedUnit}
                        onChange={setPreparedUnit}
                        options={units.map((u) => ({
                          value: u.primary_id,
                          label: u.short_code,
                        }))}
                        placeholder="Select Prepared Unit"
                        styles={customSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                      {errors.preparedUnit && (
                        <p className="text-xs text-destructive flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {errors.preparedUnit}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        Other recipes must use this same unit family
                      </p>
                    </div>
                  </>
                )}

                {/* Recipe Name */}
                <div className="space-y-2">
                  <Label>
                    Recipe Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    placeholder={
                      recipeType?.value === "Sub Recipe"
                        ? "e.g. Manchurian Gravy"
                        : "Fills in from your selection"
                    }
                    value={recipeName}
                    onChange={(e) => setRecipeName(e.target.value)}
                    disabled={recipeType?.value !== "Sub Recipe"}
                    className={
                      recipeType?.value !== "Sub Recipe"
                        ? "bg-muted cursor-not-allowed"
                        : errors.recipeName
                          ? "border-destructive"
                          : ""
                    }
                  />
                  {errors.recipeName && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {errors.recipeName}
                    </p>
                  )}
                </div>

                {/* Recipe Code */}
                <div className="space-y-2">
                  <Label>Recipe Code</Label>
                  <Input
                    value={recipeCode}
                    disabled
                    className="bg-muted cursor-not-allowed"
                  />
                  <p className="text-xs text-muted-foreground">
                    Generated automatically
                  </p>
                </div>

                {/* Active */}
                <div className="space-y-2">
                  <Label>
                    Active Status <span className="text-destructive">*</span>
                  </Label>
                  <div className="flex items-center space-x-2 pt-2">
                    <Switch checked={isActive} onCheckedChange={setIsActive} />
                    <Label className="text-sm font-normal">
                      {isActive ? "Active" : "Inactive"}
                    </Label>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ---------- Ingredients ---------- */}
          <Card>
            <CardHeader className="bg-muted/30">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Ingredients &amp; Raw Materials
                </CardTitle>
                <Button onClick={handleAddIngredient} className="gap-2" size="sm">
                  <Plus className="w-4 h-4" />
                  Add Ingredient
                </Button>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {errors.ingredients && (
                <div className="mb-4 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                  <p className="text-sm text-destructive flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {errors.ingredients}
                  </p>
                </div>
              )}

              <div className="rounded-lg border border-border overflow-visible">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableHead className="w-[28%]">
                        Ingredient <span className="text-destructive">*</span>
                      </TableHead>
                      <TableHead className="w-[18%]">
                        Unit <span className="text-destructive">*</span>
                      </TableHead>
                      <TableHead className="w-[18%]">
                        Quantity <span className="text-destructive">*</span>
                      </TableHead>
                      <TableHead className="w-[14%]">Yield</TableHead>
                      <TableHead className="w-[14%]">Wasted Quantity</TableHead>
                      <TableHead className="w-[8%] text-center">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ingredients.map((row, index) => {
                      const unitOpts = getUnitOptionsForRow(row);
                      // recipe unit / sub-recipe pe yield-waste apply nahi hota
                      const noYieldRow = row.isRecipeUnit || row.isSubRecipe;
                      const baseName =
                        unitById(row.baseUnitId)?.short_code || "";

                      const convertedQty =
                        row.isRecipeUnit && num(row.quantity) > 0
                          ? (
                              num(row.quantity) *
                              (row.conversionUsed > 0
                                ? row.conversionUsed
                                : row.recipeUnitQty)
                            ).toFixed(3)
                          : null;

                      const qtyPlaceholder =
                        row.isSubRecipe && row.subPreparedQty > 0
                          ? `of ${row.subPreparedQty} ${row.subPreparedUnitName}`
                          : "0";

                      return (
                        <TableRow key={row.id}>
                          <TableCell>
                            <ReactSelect
                              value={row.ingredient}
                              onChange={(opt) =>
                                handleIngredientChange(index, opt)
                              }
                              options={ingredientOptions}
                              placeholder="Select Ingredient"
                              isClearable
                              isLoading={
                                isLoadingData && backendIngredients.length === 0
                              }
                              styles={customSelectStyles}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                            {row.isSubRecipe && (
                              <span className="text-xs text-primary font-medium mt-1 inline-block">
                                Sub-Recipe
                              </span>
                            )}
                          </TableCell>

                          <TableCell>
                            <Select
                              value={row.unitValue || ""}
                              onValueChange={(v) => handleUnitChange(index, v)}
                              disabled={!row.ingredient}
                            >
                              <SelectTrigger
                                className={
                                  !row.ingredient
                                    ? "bg-muted cursor-not-allowed"
                                    : ""
                                }
                              >
                                <SelectValue placeholder="Select Unit" />
                              </SelectTrigger>
                              <SelectContent>
                                {unitOpts.map((o) => (
                                  <SelectItem key={o.value} value={o.value}>
                                    {o.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {row.isRecipeUnit && (
                              <span className="text-xs text-primary font-medium mt-1 inline-block">
                                Recipe unit
                              </span>
                            )}
                          </TableCell>

                          <TableCell>
                            <Input
                              type="text"
                              inputMode="decimal"
                              placeholder={qtyPlaceholder}
                              value={row.quantity}
                              onChange={(e) =>
                                handleQuantityChange(index, e.target.value)
                              }
                            />
                            {convertedQty && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {row.quantity} {row.recipeUnitName} ={" "}
                                {convertedQty} {baseName}
                              </p>
                            )}
                            {row.isSubRecipe && row.subPreparedQty > 0 && (
                              <p className="text-xs text-muted-foreground mt-1">
                                One lot makes {row.subPreparedQty}{" "}
                                {row.subPreparedUnitName}
                              </p>
                            )}
                          </TableCell>

                          <TableCell>
                            <Input
                              type="text"
                              inputMode="decimal"
                              placeholder={noYieldRow ? "—" : "0"}
                              value={noYieldRow ? "" : row.yield}
                              disabled={noYieldRow}
                              className={
                                noYieldRow
                                  ? "bg-muted cursor-not-allowed"
                                  : ""
                              }
                              onChange={(e) =>
                                handleYieldChange(index, e.target.value)
                              }
                            />
                          </TableCell>

                          <TableCell>
                            <Input
                              type="text"
                              placeholder={noYieldRow ? "—" : "0"}
                              readOnly
                              disabled={noYieldRow}
                              className={
                                noYieldRow
                                  ? "bg-muted cursor-not-allowed"
                                  : "bg-muted/50"
                              }
                              value={noYieldRow ? "" : row.wasteQuantity}
                            />
                          </TableCell>

                          <TableCell className="text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteIngredient(index)}
                              disabled={ingredients.length === 1}
                              className="hover:bg-destructive/10 hover:text-destructive"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-lg space-y-2">
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Yield:</strong> how much
                  usable output you get from the input quantity. 200 g onion
                  with a yield of 170 g means 30 g is peel and waste.
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Sub-recipes:</strong>{" "}
                  enter how much of it this dish needs. Raw ingredients are cut
                  in proportion to its prepared quantity.
                </p>
                <p className="text-sm text-muted-foreground">
                  <strong className="text-foreground">Recipe units:</strong>{" "}
                  units like Spoon or Leaf come from the ingredient's own setup.
                  Yield and waste don't apply to them.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* ---------- Actions ---------- */}
          <div className="flex items-center justify-end gap-4">
            <Button
              variant="outline"
              onClick={handleCancel}
              disabled={showLoaderAdmin}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={showLoaderAdmin || isLoadingData}
              className="gap-2 gradient-primary text-primary-foreground hover:opacity-90"
            >
              {showLoaderAdmin ? (
                "Saving..."
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditMode ? "Update Recipe" : "Create Recipe"}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Ingredient</AlertDialogTitle>
            <AlertDialogDescription>
              This ingredient will be removed from the recipe.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteIngredient}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {showLoaderAdmin && <Loader />}
    </MainLayout>
  );
}